/**
 * DialIQStatusCard — the single canonical surface for DialIQ during a
 * predictive session. Replaces the old identity-card + sidebar + rhythm-bar
 * stack with one card that shows:
 *
 *   - DialIQ branding (always)
 *   - Generic "smart queue" copy when nothing's in flight
 *   - The next lead's name + live state when one is queued/calling/ready
 *   - A quiet amber notice if quality protection has thrown the kill switch
 */
"use client";

import React from "react";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";

type PredialState = "idle" | "dialing" | "ringing" | "holding" | "bridging" | "bridged" | "abandoned";

interface Props {
  predialState: PredialState;
  predialLead: Lead | null;
  isThrottleKilled: boolean;
  /** True for the Compact Widget view, false for the Full Page view. */
  compact?: boolean;
}

export function DialIQStatusCard({ predialState, predialLead, isThrottleKilled, compact }: Props) {
  const hasLead = !!predialLead && predialState !== "idle" && predialState !== "abandoned";

  // Throttle-killed wins over everything else
  if (isThrottleKilled) {
    return (
      <div
        className={`rounded-md border border-amber-200 bg-amber-50 flex items-center gap-2 ${
          compact ? "px-3 py-2" : "px-4 py-3 shadow-sm"
        }`}
      >
        <span className={`rounded-full bg-amber-500 flex-shrink-0 ${compact ? "h-1.5 w-1.5" : "h-2 w-2"}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-gray-900 ${compact ? "text-[11px]" : "text-sm"}`}>
            DialIQ paused
          </div>
          <div className={`text-amber-900 ${compact ? "text-[10px]" : "text-[11px]"}`}>
            Predictive dialing paused to protect your call quality.
          </div>
        </div>
      </div>
    );
  }

  const leadName = predialLead?.name?.trim() || predialLead?.phone || null;
  const stateLabel = hasLead ? stateCopy(predialState) : "Smart queue. Next lead instant.";

  return (
    <div
      className={`rounded-md border border-gray-200 bg-white flex items-center gap-2 ${
        compact ? "px-3 py-2" : "px-4 py-3 shadow-sm"
      }`}
    >
      <span className={`relative flex flex-shrink-0 ${compact ? "h-1.5 w-1.5" : "h-2 w-2"}`}>
        <span
          className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${dotColor(
            predialState,
            hasLead,
          )}`}
        />
        <span
          className={`relative inline-flex rounded-full ${dotColor(predialState, hasLead)} ${
            compact ? "h-1.5 w-1.5" : "h-2 w-2"
          }`}
        />
      </span>

      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className={`font-semibold text-gray-900 ${compact ? "text-[11px]" : "text-sm"}`}>
          DialIQ
        </span>

        {hasLead && leadName ? (
          <>
            <span className={`text-gray-300 ${compact ? "text-[10px]" : "text-xs"}`}>·</span>
            <span className={`text-gray-900 truncate ${compact ? "text-[11px]" : "text-sm"}`}>
              {leadName}
            </span>
            <span className={`text-gray-300 ${compact ? "text-[10px]" : "text-xs"}`}>·</span>
            <span className={`text-gray-500 ${compact ? "text-[10px]" : "text-xs"}`}>
              {stateLabel}
            </span>
          </>
        ) : (
          <>
            <span className={`text-gray-300 ${compact ? "text-[10px]" : "text-xs"}`}>·</span>
            <span className={`text-gray-600 ${compact ? "text-[10px]" : "text-xs"}`}>
              {stateLabel}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function stateCopy(state: PredialState): string {
  switch (state) {
    case "dialing":   return "calling…";
    case "ringing":   return "ringing";
    case "holding":   return "ready when you hang up";
    case "bridging":  return "connecting…";
    case "bridged":   return "live";
    default:          return "queued";
  }
}

function dotColor(state: PredialState, hasLead: boolean): string {
  if (!hasLead) return "bg-violet-500";
  switch (state) {
    case "holding":   return "bg-emerald-500";
    case "bridging":
    case "bridged":   return "bg-blue-500";
    case "ringing":
    case "dialing":   return "bg-amber-500";
    default:          return "bg-violet-500";
  }
}
